import type { Address } from 'viem';
import type {
  MockPalmCredentialRequest,
  MockPalmCredentialResponse,
  VerifyCredentialRequest,
  VerifyCredentialResponse,
  VerificationFlowResult,
  UserCheckResult,
} from '../types/verification';

/**
 * VerificationService - Backend verification API service
 *
 * Responsibilities:
 * - Generate mock palm credential
 * - Verify user credential
 *
 * API Endpoints:
 * - POST /v1/airdrop-demo/mock-palm-credential - Mock palm credential
 * - POST /v1/verification-requests/verify - Verify credential
 */
export class VerificationService {
  private baseUrl: string;
  private apiKey: string;

  constructor() {
    this.baseUrl = 'https://app.sandbox.humanity.org/api';
    this.apiKey = 'hp_QsEGJD3jhxJ8nE8uq7Kpa6LASbPue5AAaJG6n4tpkoa';
  }

  private get headers(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      'x-hp-api-key': this.apiKey,
    };
  }

  /**
   * Check if a user exists in the airdrop system
   *
   * @param walletAddress - User wallet address
   * @returns UserCheckResult indicating whether user exists
   */
  async checkUser(walletAddress: Address): Promise<UserCheckResult> {
    const url = `${this.baseUrl}/v1/airdrop-demo/user?walletAddress=${walletAddress}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: this.headers,
    });

    if (response.status === 404) {
      return { exists: false };
    }

    if (!response.ok) {
      throw new Error(`Failed to check user: ${response.statusText}`);
    }

    const data = await response.json();
    return { exists: true, data };
  }

  /**
   * Generate mock palm credential
   *
   * @param walletAddress - User wallet address
   * @param palmType - Palm type (default 1)
   * @returns Mock palm credential response
   */
  async mockPalmCredential(
    walletAddress: Address,
    palmType: number = 1
  ): Promise<MockPalmCredentialResponse> {
    const url = `${this.baseUrl}/v1/airdrop-demo/mock-palm-credential`;

    const requestBody: MockPalmCredentialRequest = {
      walletAddress,
      palmType,
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`Failed to generate palm credential: ${response.statusText}`);
    }

    return response.json() as Promise<MockPalmCredentialResponse>;
  }

  /**
   * Verify user credential
   *
   * @param userAddress - User address
   * @param requiredClaims - Required claims (default ["humanity_identity"])
   * @returns Verify credential response
   */
  async verifyCredential(
    userAddress: Address,
    requiredClaims: string[] = ['humanity_identity']
  ): Promise<VerifyCredentialResponse> {
    const url = `${this.baseUrl}/v1/verification-requests/verify`;

    const requestBody: VerifyCredentialRequest = {
      userAddress,
      requiredClaims,
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`Failed to verify credential: ${response.statusText}`);
    }

    return response.json() as Promise<VerifyCredentialResponse>;
  }

  /**
   * Execute verification flow
   *
   * Steps:
   * 1. Generate mock palm credential
   * 2. Verify credential
   *
   * @param walletAddress - User wallet address
   * @returns Verification flow result
   */
  async executeVerificationFlow(walletAddress: Address): Promise<VerificationFlowResult> {
    try {
      // Step 1: Generate mock palm credential
      console.log('[VerificationService] Step 1: Generating palm credential for', walletAddress);
      const palmResult = await this.mockPalmCredential(walletAddress, 1);
      console.log('[VerificationService] Palm credential generated:', palmResult);

      if (!palmResult.success) {
        return {
          success: false,
          message: palmResult.message ?? 'Failed to generate palm credential.',
        };
      }

      // Step 2: Verify credential
      console.log('[VerificationService] Step 2: Verifying credential');
      const verifyResult = await this.verifyCredential(walletAddress, ['humanity_identity']);
      console.log('[VerificationService] Verification result:', verifyResult);

      if (verifyResult.isVerified) {
        return {
          success: true,
          message: 'Verification completed successfully! You can now claim your airdrop.',
        };
      } else {
        return {
          success: false,
          message: verifyResult.message ?? 'Verification failed. Please try again later.',
        };
      }
    } catch (error) {
      console.error('[VerificationService] Verification flow failed:', error);
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred during verification. Please try again.',
      };
    }
  }
}

/**
 * Service factory function
 *
 * @returns VerificationService instance
 */
export function createVerificationService(): VerificationService {
  return new VerificationService();
}
